import type { TransitionEvent } from "react";
import { useCallback, useEffect, useState } from "react";

export const BANNER_REVEAL_ANIMATION_MS = 250;

/**
 * Survives `PageTopBanner` remounts on SPA navigation so we skip the entrance animation
 * after the first successful reveal in the tab session. Resets on full reload.
 */
const pageTopBannerRevealSession = { hasPlayed: false };

interface IUseBannerRevealAnimationParams {
    //  Target height in pixels. The container animates from 0 to this value.
    targetHeight: number;
}

interface IUseBannerRevealAnimationApi {
    animatedHeight: number;
    isContentVisible: boolean;
    onContainerTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => void;
}

/**
 * Drives height-then-opacity reveal state. Used by `PageTopBannerRevealProvider`
 * (in `bannerRevealContext.tsx`); banner implementations consume the result via
 * `usePageTopBannerStripReveal`.
 */
export const useBannerRevealAnimation = ({
    targetHeight,
}: IUseBannerRevealAnimationParams): IUseBannerRevealAnimationApi => {
    const [animatedHeight, setAnimatedHeight] = useState(() =>
        pageTopBannerRevealSession.hasPlayed && targetHeight > 0 ? targetHeight : 0,
    );
    const [isContentVisible, setIsContentVisible] = useState(
        () => pageTopBannerRevealSession.hasPlayed && targetHeight > 0,
    );

    useEffect(() => {
        if (targetHeight === 0) {
            setAnimatedHeight(0);
            setIsContentVisible(false);
            return;
        }

        if (pageTopBannerRevealSession.hasPlayed) {
            setAnimatedHeight(targetHeight);
            setIsContentVisible(true);
            return;
        }

        const rafId = requestAnimationFrame(() => {
            setAnimatedHeight(targetHeight);
        });

        return () => cancelAnimationFrame(rafId);
    }, [targetHeight]);

    useEffect(() => {
        if (
            pageTopBannerRevealSession.hasPlayed ||
            !isContentVisible ||
            animatedHeight !== targetHeight ||
            targetHeight <= 0
        ) {
            return;
        }

        pageTopBannerRevealSession.hasPlayed = true;
    }, [animatedHeight, isContentVisible, targetHeight]);

    const onContainerTransitionEnd = useCallback(
        (event: TransitionEvent<HTMLDivElement>) => {
            if (event.propertyName !== "height") {
                return;
            }

            if (animatedHeight === targetHeight && targetHeight > 0) {
                setIsContentVisible(true);
            }
        },
        [animatedHeight, targetHeight],
    );

    return { animatedHeight, isContentVisible, onContainerTransitionEnd };
};
